'use client';

import type { default as PdfViewerType } from '@/elements/pdf-viewer/pdf-viewer';
import { Loader2, RotateCcw } from 'lucide-react';
import dynamic from 'next/dynamic';
import React, { useState, useCallback, useEffect } from 'react';
import { VirtualizedHtmlContent } from './VirtualizedHtmlContent';

const PdfViewer = dynamic(() => import('@/elements/pdf-viewer/pdf-viewer'), {
  ssr: false,
  loading: () => (
    <div className='flex items-center justify-center h-full'>
      <Loader2 className='w-4 h-4 animate-spin text-brand-tertiary' />
    </div>
  ),
}) as typeof PdfViewerType;
import { usePreviewContent } from '@/hooks/usePreviewContent';
import { useAppSelector } from '@/lib/redux/hooks';
import { useTranslations } from 'next-intl';
import { ExternalLinkPreview } from './ExternalLinkPreview';
import { LinkActionsButton } from './LinkActionsButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * TextPreview Component
 *
 * Renders document previews for both hover tooltips and the references sidebar.
 * This component is used in two distinct contexts:
 *
 * 1. **Hover Preview System** (via CustomHoverPreview in CommonMarkdown)
 *    - Transient floating previews near cursor
 *    - Shown when hovering citation links in chat
 *    - Uses 'hover' highlight mode (purple outline)
 *    - Automatically disabled when sidebar is expanded
 *
 * 2. **References Sidebar System** (via CitationItem in ReferenceSidebar)
 *    - Persistent embedded previews in sidebar
 *    - Always visible when sidebar is expanded
 *    - Uses 'active' highlight mode (yellow background)
 *    - Supports bi-directional highlighting with chat
 *
 * Both systems share the same rendering logic (via usePreviewContent hook)
 * but serve different user interaction patterns.
 */

interface TextPreviewProps {
  href: string;
  locale: string;
  width?: number | undefined;
  height?: number | undefined;
  embedded?: boolean | undefined; // Kept for backwards compatibility, but now unused
  resetScrollTrigger?: number | undefined; // When this changes, reset scroll to highlighted section
  onSectionNotFound?: ((notFound: boolean) => void) | undefined; // Callback when section is not found
  highlightMode?: 'active' | 'hover' | undefined; // Type of highlight: 'active' = persistent box, 'hover' = transient outline
  citationNumber?: number | undefined; // Citation number from research sidebar
  citationUrl?: string | undefined; // Citation processed URL
  isHighlighted?: boolean | undefined; // Whether this citation is currently highlighted
  onCopyLinkOpenChange?: ((open: boolean) => void) | undefined; // Callback when CopyLinkButton dropdown state changes
  onLinkOptionsOpenChange?: ((open: boolean) => void) | undefined; // Callback when LinkOptionsButton dropdown state changes
  onTitleClick?: (() => void) | undefined; // Callback when title is clicked (for sidebar citations) - deprecated, use titleHref instead
  hideContent?: boolean | undefined; // When true, only show header without document content (for external link mode)
  titleHref?: string | undefined; // URL to use for title link (internal or external based on reference type)
  sessionId?: string | undefined; // Session ID for tracking in Discord alerts
  priority?: 'high' | 'normal' | undefined; // Request priority: 'high' for hover previews (processed first), 'normal' for sidebar tiles
  showIconsOnHover?: boolean | undefined; // When true, show action icons only on hover (for sidebar tiles)
  onResetScroll?: (() => void) | undefined; // Callback to reset scroll position to highlighted section
  pdfScale?: number | undefined; // Initial zoom scale for PDF previews (default 1.5)
}

export const TextPreview = React.memo(
  function TextPreview({
    href,
    locale,
    width = 400,
    height = 300,
    resetScrollTrigger = 0,
    onSectionNotFound,
    highlightMode = 'active', // Default to active highlight for backward compatibility
    citationNumber,
    isHighlighted = false,
    onCopyLinkOpenChange,
    onLinkOptionsOpenChange,
    onTitleClick,
    hideContent = false,
    titleHref,
    sessionId,
    priority = 'normal', // Default to normal priority for sidebar
    showIconsOnHover = false, // Default to always show icons
    onResetScroll,
    pdfScale,
  }: TextPreviewProps) {
    const t = useTranslations('TextPreview');

    // Initialize as null to distinguish between "not checked yet" and "checked and not found"
    const [sectionNotFound, setSectionNotFound] = useState<boolean | null>(
      null
    );

    // Get streaming state from Redux to control when to send Discord alerts
    const streaming = useAppSelector(
      (state) => state.researchSession.streaming
    );

    // Use shared hook for fetching document content
    // Pass priority to ensure hover previews are processed first
    const {
      loading,
      error,
      content,
      sectionId,
      combinedHtmlContent,
      isExternal,
      externalUrlInfo,
    } = usePreviewContent(href, locale, priority);

    // Reset section not found state when sectionId or content changes
    useEffect(() => {
      setSectionNotFound(null);
    }, [sectionId, combinedHtmlContent]);

    // Notify parent when section not found state changes
    useEffect(() => {
      if (onSectionNotFound && sectionNotFound !== null) {
        onSectionNotFound(sectionNotFound);
      }
    }, [sectionNotFound, onSectionNotFound]);

    // Handle section scroll callback from VirtualizedHtmlContent
    const handleScrollToSection = useCallback(
      (_sectionId: string, found: boolean) => {
        setSectionNotFound(!found);

        // Only send Discord alert if section is NOT in source HTML
        // This prevents false positives from virtualization/timing issues
        if (!found && streaming && combinedHtmlContent) {
          import('@/lib/utils/document-validation')
            .then(({ reportSectionNotFoundSync, validateSectionExists }) => {
              // Pre-validate: Check if section exists in source HTML
              const existsInSource = validateSectionExists(
                _sectionId,
                combinedHtmlContent
              );

              if (!existsInSource) {
                // True "not found" - section doesn't exist in source HTML
                reportSectionNotFoundSync(_sectionId, href, {
                  componentName: 'text_preview',
                  sessionId,
                });
              }
              // If existsInSource is true, it's a virtualization/timing issue - don't alert
            })
            .catch(() => {});
        }
      },
      [href, streaming, sessionId, combinedHtmlContent]
    );

    // When hideContent is true, show only header (no wrapper needed)
    if (hideContent && !loading && !error && content.documentTitle) {
      return (
        <div
          className='w-full bg-surface-card shadow-sm'
          style={{ maxWidth: `${width}px` }}
        >
          <div className='bg-surface-subtle px-2 py-1 flex items-center gap-1.5'>
            {citationNumber && (
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${
                  isHighlighted
                    ? 'bg-brand-tertiary-hover text-content-inverse'
                    : 'bg-brand-tertiary text-content-inverse'
                }`}
              >
                {citationNumber}
              </div>
            )}
            <div className='text-xs font-semibold text-brand-tertiary dark:text-brand-tertiary-color truncate flex-1 min-w-0 leading-tight'>
              {titleHref ? (
                <a
                  href={titleHref}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:text-brand-tertiary-hover dark:hover:text-brand-tertiary-color hover:underline'
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {content.documentTitle}
                </a>
              ) : onTitleClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTitleClick();
                  }}
                  className='hover:text-brand-tertiary-hover dark:hover:text-brand-tertiary-color hover:underline text-left'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onTitleClick();
                    }
                  }}
                >
                  {content.documentTitle}
                </button>
              ) : (
                content.documentTitle
              )}
            </div>
            <div
              className={`flex items-center gap-1 flex-shrink-0 ${showIconsOnHover ? 'opacity-0 group-hover:opacity-100 transition-opacity' : ''}`}
            >
              {onResetScroll && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onResetScroll();
                        }}
                        className='h-5 w-5 flex items-center justify-center hover:bg-brand-tertiary/10 dark:hover:bg-brand-tertiary-color/10 rounded cursor-pointer'
                        aria-label={t('resetScrollPosition')}
                      >
                        <RotateCcw className='h-3.5 w-3.5 text-brand-tertiary dark:text-brand-tertiary-color' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('resetScrollPosition')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <LinkActionsButton
                href={href}
                sectionId={sectionId}
                onOpenChange={onLinkOptionsOpenChange}
                originalSource={content.originalSource}
                mode='open'
              />
              <LinkActionsButton
                href={href}
                sectionId={sectionId}
                onOpenChange={onCopyLinkOpenChange}
                originalSource={content.originalSource}
                mode='copy'
              />
            </div>
          </div>
        </div>
      );
    }

    // Render external link preview for external URLs (single compact row)
    if (isExternal && externalUrlInfo) {
      return (
        <ExternalLinkPreview
          urlInfo={externalUrlInfo}
          width={width}
          citationNumber={citationNumber}
          isHighlighted={isHighlighted}
        />
      );
    }

    return (
      <div
        className='w-full bg-surface-card shadow-sm flex flex-col'
        style={{
          maxWidth: `${width}px`,
          height: `${height}px`,
        }}
      >
        {loading && (
          <div className='flex-1 flex items-center justify-center py-4'>
            <Loader2 className='w-4 h-4 animate-spin text-brand-tertiary dark:text-brand-tertiary-color' />
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 px-3 py-2'>
            {citationNumber && (
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${
                  isHighlighted
                    ? 'bg-brand-tertiary-hover text-content-inverse'
                    : 'bg-brand-tertiary text-content-inverse'
                }`}
              >
                {citationNumber}
              </div>
            )}
            <div className='text-xs text-content-muted'>{error}</div>
          </div>
        )}

        {!loading &&
          !error &&
          (combinedHtmlContent || content.pdfUrl || content.imageUrl) && (
            <div className='relative h-full flex flex-col'>
              {content.documentTitle && (
                <div className='bg-surface-subtle px-2 py-1 flex items-center gap-1.5'>
                  {citationNumber && (
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${
                        isHighlighted
                          ? 'bg-brand-tertiary-hover text-content-inverse'
                          : 'bg-brand-tertiary text-content-inverse'
                      }`}
                    >
                      {citationNumber}
                    </div>
                  )}
                  <div className='text-xs font-semibold text-brand-tertiary dark:text-brand-tertiary-color truncate flex-1 min-w-0 leading-tight'>
                    {titleHref ? (
                      <a
                        href={titleHref}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='hover:text-brand-tertiary-hover dark:hover:text-brand-tertiary-color hover:underline'
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {content.documentTitle}
                      </a>
                    ) : onTitleClick ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTitleClick();
                        }}
                        className='hover:text-brand-tertiary-hover dark:hover:text-brand-tertiary-color hover:underline text-left'
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onTitleClick();
                          }
                        }}
                      >
                        {content.documentTitle}
                      </button>
                    ) : (
                      content.documentTitle
                    )}
                  </div>
                  <div
                    className={`flex items-center gap-1 flex-shrink-0 ${showIconsOnHover ? 'opacity-0 group-hover:opacity-100 transition-opacity' : ''}`}
                  >
                    {onResetScroll && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onResetScroll();
                        }}
                        className='h-5 w-5 flex items-center justify-center hover:bg-brand-tertiary/10 dark:hover:bg-brand-tertiary-color/10 rounded cursor-pointer'
                        aria-label='Reset scroll position'
                      >
                        <RotateCcw className='h-3.5 w-3.5 text-brand-tertiary dark:text-brand-tertiary-color' />
                      </button>
                    )}
                    <LinkActionsButton
                      href={href}
                      sectionId={sectionId}
                      onOpenChange={onLinkOptionsOpenChange}
                      originalSource={content.originalSource}
                      mode='open'
                    />
                    <LinkActionsButton
                      href={href}
                      sectionId={sectionId}
                      onOpenChange={onCopyLinkOpenChange}
                      originalSource={content.originalSource}
                      mode='copy'
                    />
                  </div>
                </div>
              )}
              <div
                className='flex-1 overflow-hidden bg-surface-subtle cursor-text select-text'
                style={{
                  contain: 'layout style paint',
                  contentVisibility: 'auto',
                }}
              >
                {combinedHtmlContent && (
                  <VirtualizedHtmlContent
                    htmlContent={combinedHtmlContent}
                    sectionId={sectionId}
                    highlightClass={
                      highlightMode === 'hover'
                        ? 'hoverOutline'
                        : 'activeHighlight'
                    }
                    onScrollToSection={handleScrollToSection}
                    resetScrollTrigger={resetScrollTrigger}
                    showScrollbarOnHover={showIconsOnHover}
                  />
                )}
                {content.pdfUrl && (
                  <PdfViewer
                    fileUrl={content.pdfUrl}
                    height='auto'
                    isOverflow={true}
                    {...(pdfScale !== undefined && { scale: pdfScale })}
                  />
                )}
                {content.imageUrl && (
                  <div className='w-full h-full flex items-center justify-center bg-surface-card'>
                    <img
                      src={content.imageUrl}
                      alt={content.documentTitle || 'Image preview'}
                      className='max-w-full max-h-full object-contain'
                      loading='lazy'
                    />
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Only re-render if these props actually change
    return (
      prevProps.href === nextProps.href &&
      prevProps.locale === nextProps.locale &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.resetScrollTrigger === nextProps.resetScrollTrigger &&
      prevProps.isHighlighted === nextProps.isHighlighted &&
      prevProps.highlightMode === nextProps.highlightMode &&
      prevProps.hideContent === nextProps.hideContent &&
      prevProps.titleHref === nextProps.titleHref &&
      prevProps.sessionId === nextProps.sessionId &&
      prevProps.citationNumber === nextProps.citationNumber &&
      prevProps.priority === nextProps.priority
      // Note: Callback props (onSectionNotFound, etc.) are excluded from comparison
      // as they typically don't affect rendering and change frequently
    );
  }
);
